import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSpring, animated, config } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import axios from 'axios';
import { Button } from './ui/button';
import { MapPin, Star, Heart, GraduationCap, Euro, ChevronUp, ChevronDown, Navigation, X, User } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl;

const typeColors = {
  service: '#D4A574',
  course: '#27AE60'
};

// Apply offset to markers sharing same coordinates - distribute in circle
const applyCoordinateOffset = (markers) => {
  // Filter only markers with valid coordinates first
  const validMarkers = markers.filter(m => 
    m && 
    typeof m.latitude === 'number' && 
    typeof m.longitude === 'number' && 
    !isNaN(m.latitude) && 
    !isNaN(m.longitude) &&
    m.latitude !== 0 && 
    m.longitude !== 0
  );
  
  const coordMap = new Map();
  
  // Group markers by coordinates (round to 3 decimals ~100m precision for grouping nearby markers)
  validMarkers.forEach((marker, index) => {
    const key = `${marker.latitude.toFixed(3)},${marker.longitude.toFixed(3)}`;
    if (!coordMap.has(key)) {
      coordMap.set(key, []);
    }
    coordMap.get(key).push(index);
  });
  
  // Apply offset to ALL markers in groups with duplicates
  const result = [...validMarkers];
  coordMap.forEach((indices) => {
    if (indices.length > 1) {
      // Small offset: ~30m - keeps markers clustered together but separates them when expanded
      const offsetDistance = 0.0003;
      const count = indices.length;
      
      // Distribute ALL markers evenly in a circle
      indices.forEach((idx, i) => {
        const angle = (2 * Math.PI * i) / count;
        const newLat = result[idx].latitude + (offsetDistance * Math.cos(angle));
        const newLng = result[idx].longitude + (offsetDistance * Math.sin(angle));
        
        // Only apply if new coordinates are valid
        if (!isNaN(newLat) && !isNaN(newLng)) {
          result[idx] = {
            ...result[idx],
            latitude: newLat,
            longitude: newLng
          };
        }
      });
    }
  });
  
  return result;
};

// Component to center map on marker - WITHOUT changing zoom
function CenterOnMarker({ position }) {
  const map = useMap();
  
  useEffect(() => {
    if (position) {
      // Pan to position without changing zoom
      map.panTo(position, { animate: true, duration: 0.5 });
    }
  }, [position, map]);
  
  return null;
}

// Component to fit bounds
function FitBounds({ markers }) {
  const map = useMap();
  
  useEffect(() => {
    if (!map || !map._container) return;
    try {
      if (markers.length > 0) {
        const validMarkers = markers.filter(m => m.latitude && m.longitude);
        if (validMarkers.length > 0) {
          const bounds = L.latLngBounds(validMarkers.map(m => [m.latitude, m.longitude]));
          map.fitBounds(bounds, { padding: [30, 30], maxZoom: 11 });
        }
      }
    } catch (e) {
      console.warn('Map fitBounds error:', e);
    }
  }, [markers, map]);
  
  return null;
}

// Create large touch-friendly marker with photo preview
const createMobileMarker = (type, isFeatured = false, isSelected = false, imageUrl = null) => {
  const color = typeColors[type] || '#D4A574';
  const size = isSelected ? 52 : 44;
  
  // If we have an image, show it as a circular thumbnail
  if (imageUrl) {
    return L.divIcon({
      className: 'mobile-marker',
      html: `
        <div style="
          width: ${size}px;
          height: ${size}px;
          border-radius: 50%;
          border: ${isSelected ? '4px' : '3px'} solid ${color};
          box-shadow: ${isSelected ? '0 0 0 3px ' + color + ', 0 4px 15px rgba(0,0,0,0.4)' : '0 3px 10px rgba(0,0,0,0.3)'};
          overflow: hidden;
          background-color: ${color};
          transition: all 0.2s ease;
          ${isFeatured ? 'animation: pulse-mobile 2s infinite;' : ''}
        ">
          <img 
            src="${imageUrl}" 
            alt="" 
            style="width: 100%; height: 100%; object-fit: cover;"
            onerror="this.style.display='none'"
          />
        </div>
      `,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2]
    });
  }
  
  // Fallback to icon if no image
  return L.divIcon({
    className: 'mobile-marker',
    html: `
      <div style="
        background-color: ${color};
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        border: ${isSelected ? '4px' : '3px'} solid white;
        box-shadow: ${isSelected ? '0 0 0 3px ' + color + ', 0 4px 15px rgba(0,0,0,0.4)' : '0 3px 10px rgba(0,0,0,0.3)'};
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        ${isFeatured ? 'animation: pulse-mobile 2s infinite;' : ''}
      ">
        <svg width="${size/2.5}" height="${size/2.5}" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          ${type === 'service' ? '<path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>' : ''}
          ${type === 'course' ? '<path d="M22 10v6M2 10l10-5 10 5-10 5z"></path><path d="M6 12v5c3 3 9 3 12 0v-5"></path>' : ''}
        </svg>
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
};

// Cluster icon for mobile
const createMobileClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  const size = count < 10 ? 44 : 52;
  
  return L.divIcon({
    html: `
      <div style="
        background: linear-gradient(135deg, #D4A574 0%, #B8956A 100%);
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 3px 10px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: 16px;
      ">${count}</div>
    `,
    className: 'mobile-cluster-icon',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
};

// Result Card Component
function ResultCard({ marker, isActive, onClick, lang, t }) {
  const getLocalizedText = (obj) => {
    if (!obj) return '';
    if (typeof obj === 'string') return obj;
    return obj[lang] || obj.es || obj.en || '';
  };

  // Get the profile link identifier (username preferred over user_id)
  const getProfileIdentifier = () => {
    if (marker.type === 'service') {
      return marker.teacher_username || marker.teacher_user_id;
    } else {
      return marker.school_username || marker.school_user_id;
    }
  };
  
  const profileIdentifier = getProfileIdentifier();
  const hasProfileLink = !!profileIdentifier;
  
  const handleProfileClick = (e) => {
    e.stopPropagation();
    if (profileIdentifier) {
      window.open(`/p/${profileIdentifier}`, '_blank');
    }
  };

  return (
    <div 
      onClick={onClick}
      className={`flex-shrink-0 w-[280px] snap-center bg-white rounded-xl shadow-lg overflow-hidden border-2 transition-all ${
        isActive ? 'border-amber-400 shadow-xl' : 'border-transparent'
      }`}
    >
      {/* Image */}
      <div className="relative h-28 bg-gradient-to-br from-amber-100 to-amber-50">
        {marker.image ? (
          <img src={marker.image} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {marker.type === 'service' ? (
              <Heart className="w-10 h-10 text-amber-300" />
            ) : (
              <GraduationCap className="w-10 h-10 text-green-300" />
            )}
          </div>
        )}
        
        {/* Type Badge */}
        <div 
          className="absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-semibold text-white"
          style={{ backgroundColor: typeColors[marker.type] }}
        >
          {marker.type === 'service' ? t('service.session') : t('course.course')}
        </div>
        
        {/* Google Rating Badge (prioritize over featured) */}
        {marker.google_rating ? (
          <div className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 bg-white/95 rounded-full shadow-sm">
            <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
            <span className="text-sm font-semibold text-gray-800">{marker.google_rating.toFixed(1)}</span>
            {marker.google_reviews_count && (
              <span className="text-xs text-gray-500">({marker.google_reviews_count})</span>
            )}
          </div>
        ) : marker.featured && (
          <div className="absolute top-2 right-2 bg-yellow-400 rounded-full p-1">
            <Star className="w-4 h-4 text-white fill-white" />
          </div>
        )}
        
        {/* Price Badge */}
        {marker.price > 0 && (
          <div className="absolute bottom-2 right-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-lg">
            <span className="font-bold text-gray-900 text-sm">€{marker.price}</span>
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-3">
        <h3 className="font-semibold text-gray-900 text-sm leading-tight line-clamp-1 mb-2">
          {getLocalizedText(marker.title)}
        </h3>
        
        {/* Professional info for services */}
        {marker.type === 'service' && marker.teacher_name && (
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                {marker.teacher_avatar ? (
                  <img src={marker.teacher_avatar} alt="" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-4 h-4 text-amber-500" />
                )}
              </div>
              <span className="text-xs text-gray-600 truncate">{marker.teacher_name}</span>
            </div>
            {hasProfileLink && (
              <button
                onClick={handleProfileClick}
                className="text-xs text-amber-600 hover:text-amber-700 flex-shrink-0 px-2 py-0.5 rounded bg-amber-50 hover:bg-amber-100 active:bg-amber-200 transition-colors"
              >
                Perfil
              </button>
            )}
          </div>
        )}
        
        {/* School info for courses */}
        {marker.type === 'course' && marker.school_name && (
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <GraduationCap className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <span className="text-xs text-gray-600 truncate">{marker.school_name}</span>
            </div>
            {hasProfileLink && (
              <button
                onClick={handleProfileClick}
                className="text-xs text-green-600 hover:text-green-700 flex-shrink-0 px-2 py-0.5 rounded bg-green-50 hover:bg-green-100 active:bg-green-200 transition-colors"
              >
                Perfil
              </button>
            )}
          </div>
        )}
        
        {/* Location */}
        <div className="flex items-center text-gray-500">
          <MapPin className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
          <span className="text-xs truncate">{marker.city}</span>
        </div>
      </div>
    </div>
  );
}

export default function MapViewMobile() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const mapRef = useRef(null);
  
  const [markers, setMarkers] = useState([]);
  const [filteredMarkers, setFilteredMarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(true);
  const [centerPosition, setCenterPosition] = useState(null);
  const [locating, setLocating] = useState(false);
  
  const scrollRef = useRef(null);
  const lang = i18n.language || 'es';

  // Bottom sheet animation
  const [{ y }, api] = useSpring(() => ({ y: 0 }));
  
  const sheetHeight = 300; // Increased height to show full card content
  const minY = 0;
  const maxY = sheetHeight - 60;

  const bind = useDrag(
    ({ last, movement: [, my], cancel, direction: [, dy] }) => {
      if (last) {
        if (my > 50 && dy > 0) {
          api.start({ y: maxY, config: config.stiff });
          setSheetOpen(false);
        } else {
          api.start({ y: minY, config: config.stiff });
          setSheetOpen(true);
        }
      } else {
        api.start({ y: Math.max(minY, Math.min(maxY, my)), immediate: true });
      }
    },
    { from: () => [0, y.get()], filterTaps: true, bounds: { top: minY, bottom: maxY }, rubberband: true }
  );

  // Load data
  useEffect(() => {
    const loadMapData = async () => {
      try {
        const [servicesRes, coursesRes] = await Promise.all([
          axios.get(`${API_URL}/api/services`),
          axios.get(`${API_URL}/api/courses`)
        ]);

        const serviceMarkers = servicesRes.data
          .filter(s => s.location?.latitude && s.location?.longitude)
          .map(s => ({
            id: s.id,
            type: 'service',
            title: s.title,
            latitude: s.location.latitude,
            longitude: s.location.longitude,
            city: s.location.city,
            province: s.location.province || '',
            area: s.location.area || '',
            image: s.images?.[0] || null,
            price: s.price || 0,
            featured: s.featured || false,
            teacher_name: s.teacher_name || null,
            teacher_avatar: s.teacher_avatar || null,
            teacher_user_id: s.teacher_user_id || null,
            teacher_username: s.teacher_username || null,
            google_rating: s.google_rating || null,
            google_reviews_count: s.google_reviews_count || null
          }));

        const courseMarkers = coursesRes.data
          .filter(c => c.location?.latitude && c.location?.longitude)
          .map(c => ({
            id: c.id,
            type: 'course',
            title: c.title,
            latitude: c.location.latitude,
            longitude: c.location.longitude,
            city: c.location.city,
            province: c.location.province || '',
            area: c.location.area || '',
            image: c.images?.[0] || null,
            price: c.price || 0,
            featured: c.featured || false,
            school_name: c.school_name || null,
            school_user_id: c.school_user_id || null,
            school_username: c.school_username || null,
            google_rating: c.google_rating || null,
            google_reviews_count: c.google_reviews_count || null
          }));

        const allMarkers = [...serviceMarkers, ...courseMarkers];
        
        // Apply offset to markers with identical coordinates
        const offsetMarkers = applyCoordinateOffset(allMarkers);
        
        setMarkers(offsetMarkers);
        setFilteredMarkers(offsetMarkers);
      } catch (error) {
        console.error('Error loading map data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMapData();
  }, []);

  // Filter markers
  useEffect(() => {
    let filtered = markers;
    if (activeFilter !== 'all') {
      filtered = filtered.filter(m => m.type === activeFilter);
    }
    setFilteredMarkers(filtered);
    setSelectedMarker(null);
  }, [markers, activeFilter]);

  // Scroll to selected card
  useEffect(() => {
    if (selectedMarker && scrollRef.current) {
      const index = filteredMarkers.findIndex(m => m.id === selectedMarker.id && m.type === selectedMarker.type);
      if (index !== -1) {
        const cardWidth = 288; // 280px + gap
        scrollRef.current.scrollTo({ left: index * cardWidth, behavior: 'smooth' });
      }
    }
  }, [selectedMarker, filteredMarkers]);

  const handleMarkerClick = (marker) => {
    setSelectedMarker(marker);
    setCenterPosition([marker.latitude, marker.longitude]);
    setSheetOpen(true);
    api.start({ y: minY, config: config.stiff });
  };

  const handleCardClick = (marker) => {
    if (selectedMarker?.id === marker.id) {
      // Double tap - save scroll position and selected marker before navigating
      if (scrollRef.current) {
        sessionStorage.setItem('mapMobileScrollPos', scrollRef.current.scrollLeft.toString());
      }
      sessionStorage.setItem('mapMobileSelectedMarkerId', marker.id);
      sessionStorage.setItem('mapMobileSelectedMarkerType', marker.type);
      navigate(marker.type === 'service' ? `/service/${marker.id}` : `/course/${marker.id}`);
    } else {
      setSelectedMarker(marker);
      setCenterPosition([marker.latitude, marker.longitude]);
    }
  };

  // Restore scroll position and selected marker when coming back
  useEffect(() => {
    const savedMarkerId = sessionStorage.getItem('mapMobileSelectedMarkerId');
    const savedMarkerType = sessionStorage.getItem('mapMobileSelectedMarkerType');
    const savedScrollPos = sessionStorage.getItem('mapMobileScrollPos');
    
    if (savedMarkerId && filteredMarkers.length > 0) {
      // Find and select the saved marker
      const savedMarker = filteredMarkers.find(m => m.id === savedMarkerId && m.type === savedMarkerType);
      if (savedMarker) {
        setSelectedMarker(savedMarker);
        setCenterPosition([savedMarker.latitude, savedMarker.longitude]);
        setSheetOpen(true);
        api.start({ y: minY, config: config.stiff });
        
        // Restore scroll position after a short delay
        if (savedScrollPos && scrollRef.current) {
          setTimeout(() => {
            if (scrollRef.current) {
              scrollRef.current.scrollLeft = parseInt(savedScrollPos, 10);
            }
          }, 100);
        }
      }
      
      // Clear saved state
      sessionStorage.removeItem('mapMobileSelectedMarkerId');
      sessionStorage.removeItem('mapMobileSelectedMarkerType');
      sessionStorage.removeItem('mapMobileScrollPos');
    }
  }, [filteredMarkers, api, minY]);

  const handleLocateMe = () => {
    setLocating(true);
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCenterPosition([position.coords.latitude, position.coords.longitude]);
          setLocating(false);
        },
        (error) => {
          console.error('Geolocation error:', error);
          alert('No se pudo obtener tu ubicación');
          setLocating(false);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      alert('Tu navegador no soporta geolocalización');
      setLocating(false);
    }
  };

  const toggleSheet = () => {
    if (sheetOpen) {
      api.start({ y: maxY, config: config.stiff });
      setSheetOpen(false);
    } else {
      api.start({ y: minY, config: config.stiff });
      setSheetOpen(true);
    }
  };

  if (loading) {
    return (
      <div className="h-[500px] flex items-center justify-center bg-gray-100 rounded-xl">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-200 border-t-amber-500 rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-gray-500 text-sm">{t('map.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-[85vh] min-h-[500px] max-h-[700px] rounded-xl overflow-hidden" data-testid="mobile-map">
      {/* Filter Pills - Fixed at top */}
      <div className="absolute top-0 left-0 right-0 z-[1000] bg-gradient-to-b from-white via-white to-transparent pb-6 pt-2 px-3">
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {[
            { key: 'all', label: t('common.all'), count: markers.length },
            { key: 'service', label: t('common.sessions'), count: markers.filter(m => m.type === 'service').length, color: typeColors.service },
            { key: 'course', label: t('nav.courses'), count: markers.filter(m => m.type === 'course').length, color: typeColors.course }
          ].map(filter => (
            <button
              key={filter.key}
              onClick={() => setActiveFilter(filter.key)}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeFilter === filter.key
                  ? 'text-white shadow-md'
                  : 'bg-white text-gray-700 shadow border border-gray-200'
              }`}
              style={activeFilter === filter.key ? { backgroundColor: filter.color || '#1F2937' } : {}}
            >
              {filter.label} ({filter.count})
            </button>
          ))}
        </div>
      </div>

      {/* Locate Me Button */}
      <button
        onClick={handleLocateMe}
        disabled={locating}
        className="absolute top-16 right-3 z-[1000] bg-white rounded-full p-3 shadow-lg border border-gray-200 active:scale-95 transition-transform"
        data-testid="locate-me-btn"
      >
        <Navigation className={`w-5 h-5 ${locating ? 'text-amber-500 animate-pulse' : 'text-gray-700'}`} />
      </button>

      {/* Map */}
      <MapContainer
        ref={mapRef}
        center={[28.1, -16.6]}
        zoom={9}
        style={{ height: '100%', width: '100%' }}
        zoomControl={false}
        attributionControl={false}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        
        <FitBounds markers={filteredMarkers} />
        {centerPosition && <CenterOnMarker position={centerPosition} />}
        
        <MarkerClusterGroup
          key={`cluster-mobile-${filteredMarkers.length}-${activeFilter}`}
          chunkedLoading
          removeOutsideVisibleBounds={false}
          iconCreateFunction={createMobileClusterIcon}
          maxClusterRadius={60}
          spiderfyOnMaxZoom={true}
          showCoverageOnHover={false}
          disableClusteringAtZoom={15}
          spiderfyDistanceMultiplier={4}
          zoomToBoundsOnClick={true}
          spiderLegPolylineOptions={{ weight: 2, color: '#D4A574', opacity: 0.7 }}
        >
          {filteredMarkers
            .filter(marker => 
              marker && 
              typeof marker.latitude === 'number' && 
              typeof marker.longitude === 'number' &&
              !isNaN(marker.latitude) && 
              !isNaN(marker.longitude)
            )
            .map((marker) => {
              const imageUrl = marker.image || marker.teacher_avatar || marker.school_avatar;
              return (
                <Marker
                  key={`${marker.type}-${marker.id}`}
                  position={[marker.latitude, marker.longitude]}
                  icon={createMobileMarker(
                    marker.type, 
                    marker.featured, 
                    selectedMarker?.id === marker.id,
                    imageUrl
                  )}
                  eventHandlers={{
                    click: () => handleMarkerClick(marker)
                  }}
                />
              );
            })}
        </MarkerClusterGroup>
      </MapContainer>

      {/* Bottom Sheet */}
      <animated.div
        style={{ y, touchAction: 'none' }}
        className="absolute bottom-0 left-0 right-0 z-[1000] bg-white rounded-t-3xl shadow-2xl"
      >
        {/* Handle - draggable area with hint */}
        <div 
          {...bind()}
          className="flex flex-col items-center py-2 cursor-grab active:cursor-grabbing touch-none"
        >
          <div className="w-12 h-1.5 bg-gray-300 rounded-full mb-1" />
          <span className="text-[10px] text-gray-400 font-medium">
            {sheetOpen ? t('map.dragToHide', 'Scorri per nascondere') : t('map.dragToShow', 'Scorri per vedere')}
          </span>
        </div>

        {/* Header with explanation */}
        <div className="flex items-center justify-between px-4 pb-2">
          <h3 className="font-semibold text-gray-900">
            {filteredMarkers.length} {filteredMarkers.length !== 1 ? t('common.results') : t('common.result')}
          </h3>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              toggleSheet();
            }} 
            className="flex items-center gap-1.5 px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-full transition-colors"
            data-testid="toggle-sheet-btn"
          >
            <span className="text-xs font-medium">
              {sheetOpen ? t('map.hideResults', 'Ocultar') : t('map.showResults', 'Ver más')}
            </span>
            {sheetOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </button>
        </div>

        {/* Horizontal Scroll Cards */}
        {/* Cards container - hidden when sheet is closed */}
        <div 
          ref={scrollRef}
          className={`flex gap-3 overflow-x-auto px-4 pb-6 snap-x snap-mandatory no-scrollbar transition-all duration-300 ${
            !sheetOpen ? 'opacity-0 h-0 overflow-hidden' : ''
          }`}
          style={{ height: sheetOpen ? sheetHeight - 60 : 0 }}
        >
          {filteredMarkers.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              {t('common.noResults')}
            </div>
          ) : (
            filteredMarkers.map((marker) => (
              <ResultCard
                key={`${marker.type}-${marker.id}`}
                marker={marker}
                isActive={selectedMarker?.id === marker.id && selectedMarker?.type === marker.type}
                onClick={() => handleCardClick(marker)}
                lang={lang}
                t={t}
              />
            ))
          )}
        </div>
      </animated.div>

      {/* CSS */}
      <style>{`
        @keyframes pulse-mobile {
          0%, 100% { box-shadow: 0 3px 10px rgba(0,0,0,0.3); }
          50% { box-shadow: 0 3px 20px rgba(212, 165, 116, 0.6); }
        }
        
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}
